import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ArrowLeft, ArrowRight, Users, Scale, AlertTriangle,
  CheckCircle, ChevronDown, ChevronRight, Info, Loader2,
  Sparkles, Eye, Copy, Filter, ArrowLeftRight, ThumbsUp, ThumbsDown,
  Search, X, FileText, Building, Briefcase, Home, DollarSign,
  Heart, Cpu, MapPin, BookOpen, Download
} from 'lucide-react';

// Contract types with their roles - expanded to support all Chilean contracts
const CONTRACT_CATEGORIES = {
  arriendos: {
    name: "Arriendos",
    icon: Home,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    contracts: [
      { id: "arriendo_habitacional", name: "Arriendo Habitacional", roles: ["arrendador", "arrendatario"] },
      { id: "arriendo_comercial", name: "Arriendo Comercial", roles: ["arrendador", "arrendatario"] },
      { id: "arriendo_bodega", name: "Arriendo de Bodega", roles: ["arrendador", "arrendatario"] },
      { id: "arriendo_estacionamiento", name: "Arriendo de Estacionamiento", roles: ["arrendador", "arrendatario"] },
      { id: "arriendo_vehiculo", name: "Arriendo de Vehículo", roles: ["arrendador", "arrendatario"] },
      { id: "arriendo_maquinaria", name: "Arriendo de Maquinaria", roles: ["arrendador", "arrendatario"] }
    ]
  },
  compraventas: {
    name: "Compraventas",
    icon: DollarSign,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    contracts: [
      { id: "compraventa_vehiculo", name: "Compraventa de Vehículo", roles: ["vendedor", "comprador"] },
      { id: "compraventa_inmueble", name: "Compraventa de Inmueble", roles: ["vendedor", "comprador"] },
      { id: "compraventa_bienes_muebles", name: "Compraventa Bienes Muebles", roles: ["vendedor", "comprador"] },
      { id: "compraventa_acciones", name: "Compraventa de Acciones", roles: ["vendedor", "comprador"] },
      { id: "compraventa_derechos", name: "Cesión de Derechos", roles: ["cedente", "cesionario"] },
      { id: "compraventa_fondo_comercio", name: "Fondo de Comercio", roles: ["vendedor", "comprador"] }
    ]
  },
  laborales: {
    name: "Laborales",
    icon: Briefcase,
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    contracts: [
      { id: "contrato_trabajo_indefinido", name: "Trabajo Indefinido", roles: ["empleador", "trabajador"] },
      { id: "contrato_trabajo_plazo_fijo", name: "Trabajo Plazo Fijo", roles: ["empleador", "trabajador"] },
      { id: "contrato_trabajo_obra_faena", name: "Trabajo por Obra", roles: ["empleador", "trabajador"] },
      { id: "contrato_honorarios", name: "Prestación de Servicios", roles: ["mandante", "prestador"] },
      { id: "finiquito_laboral", name: "Finiquito Laboral", roles: ["empleador", "trabajador"] },
      { id: "contrato_teletrabajo", name: "Teletrabajo", roles: ["empleador", "trabajador"] },
      { id: "contrato_aprendizaje", name: "Contrato de Aprendizaje", roles: ["empleador", "aprendiz"] }
    ]
  },
  comerciales: {
    name: "Comerciales",
    icon: Building,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    contracts: [
      { id: "nda_confidencialidad", name: "NDA / Confidencialidad", roles: ["parte_divulgadora", "parte_receptora"] },
      { id: "constitucion_spa", name: "Constitución SpA", roles: ["accionista"] },
      { id: "contrato_distribucion", name: "Distribución", roles: ["proveedor", "distribuidor"] },
      { id: "contrato_franquicia", name: "Franquicia", roles: ["franquiciante", "franquiciado"] },
      { id: "contrato_agencia", name: "Agencia Comercial", roles: ["principal", "agente"] },
      { id: "contrato_joint_venture", name: "Joint Venture", roles: ["parte_a", "parte_b"] },
      { id: "contrato_suministro", name: "Suministro", roles: ["proveedor", "cliente"] }
    ]
  },
  civiles: {
    name: "Civiles",
    icon: FileText,
    color: "text-slate-400",
    bgColor: "bg-slate-500/10",
    contracts: [
      { id: "poder_simple", name: "Poder Simple", roles: ["mandante", "mandatario"] },
      { id: "poder_amplio", name: "Poder Amplio", roles: ["mandante", "mandatario"] },
      { id: "comodato", name: "Comodato", roles: ["comodante", "comodatario"] },
      { id: "transaccion", name: "Transacción", roles: ["parte_a", "parte_b"] },
      { id: "reconocimiento_deuda", name: "Reconocimiento Deuda", roles: ["acreedor", "deudor"] },
      { id: "contrato_deposito", name: "Depósito", roles: ["depositante", "depositario"] }
    ]
  },
  familia: {
    name: "Familia",
    icon: Heart,
    color: "text-pink-400",
    bgColor: "bg-pink-500/10",
    contracts: [
      { id: "capitulaciones_matrimoniales", name: "Capitulaciones Matrimoniales", roles: ["conyuge_1", "conyuge_2"] },
      { id: "acuerdo_union_civil", name: "Acuerdo Unión Civil", roles: ["conviviente_1", "conviviente_2"] },
      { id: "acuerdo_regulacion_relaciones", name: "Regulación de Relaciones", roles: ["padre", "madre"] },
      { id: "convenio_alimentos", name: "Pensión de Alimentos", roles: ["alimentante", "alimentario"] }
    ]
  },
  financieros: {
    name: "Financieros",
    icon: DollarSign,
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/10",
    contracts: [
      { id: "mutuo_dinero", name: "Préstamo de Dinero", roles: ["mutuante", "mutuario"] },
      { id: "acuerdo_pago", name: "Acuerdo de Pago", roles: ["acreedor", "deudor"] },
      { id: "contrato_prenda", name: "Prenda", roles: ["acreedor_prendario", "deudor_prendario"] },
      { id: "contrato_fianza", name: "Fianza", roles: ["acreedor", "fiador"] }
    ]
  },
  tecnologia: {
    name: "Tecnología",
    icon: Cpu,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    contracts: [
      { id: "contrato_desarrollo_software", name: "Desarrollo de Software", roles: ["cliente", "desarrollador"] },
      { id: "contrato_saas", name: "SaaS", roles: ["proveedor", "cliente"] },
      { id: "licencia_software", name: "Licencia Software", roles: ["licenciante", "licenciatario"] },
      { id: "contrato_hosting", name: "Hosting Web", roles: ["proveedor", "cliente"] }
    ]
  },
  inmobiliarios: {
    name: "Inmobiliarios",
    icon: MapPin,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    contracts: [
      { id: "promesa_compraventa_inmueble", name: "Promesa Compraventa", roles: ["promitente_vendedor", "promitente_comprador"] },
      { id: "contrato_corretaje", name: "Corretaje Inmobiliario", roles: ["mandante", "corredor"] },
      { id: "acta_entrega_inmueble", name: "Acta de Entrega", roles: ["entregante", "receptor"] },
      { id: "contrato_administracion", name: "Administración Inmueble", roles: ["propietario", "administrador"] }
    ]
  },
  propiedad_intelectual: {
    name: "Propiedad Intelectual",
    icon: BookOpen,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/10",
    contracts: [
      { id: "cesion_derechos_autor", name: "Cesión Derechos de Autor", roles: ["cedente", "cesionario"] },
      { id: "licencia_marca", name: "Licencia de Marca", roles: ["licenciante", "licenciatario"] }
    ]
  }
};

// Helper to get role display name
const getRoleDisplayName = (roleId) => {
  const roleNames = {
    arrendador: "Arrendador (Dueño)",
    arrendatario: "Arrendatario (Inquilino)",
    vendedor: "Vendedor",
    comprador: "Comprador",
    cedente: "Cedente",
    cesionario: "Cesionario",
    empleador: "Empleador",
    trabajador: "Trabajador",
    mandante: "Mandante",
    prestador: "Prestador de Servicios",
    aprendiz: "Aprendiz",
    parte_divulgadora: "Parte Divulgadora",
    parte_receptora: "Parte Receptora",
    accionista: "Accionista",
    proveedor: "Proveedor",
    distribuidor: "Distribuidor",
    franquiciante: "Franquiciante",
    franquiciado: "Franquiciado",
    principal: "Principal",
    agente: "Agente",
    parte_a: "Parte A",
    parte_b: "Parte B",
    cliente: "Cliente",
    mandatario: "Mandatario",
    comodante: "Comodante",
    comodatario: "Comodatario",
    acreedor: "Acreedor",
    deudor: "Deudor",
    depositante: "Depositante",
    depositario: "Depositario",
    conyuge_1: "Cónyuge 1",
    conyuge_2: "Cónyuge 2",
    conviviente_1: "Conviviente 1",
    conviviente_2: "Conviviente 2",
    padre: "Padre",
    madre: "Madre",
    alimentante: "Alimentante",
    alimentario: "Alimentario",
    mutuante: "Mutuante (Prestamista)",
    mutuario: "Mutuario (Deudor)",
    acreedor_prendario: "Acreedor Prendario",
    deudor_prendario: "Deudor Prendario",
    fiador: "Fiador",
    desarrollador: "Desarrollador",
    licenciante: "Licenciante",
    licenciatario: "Licenciatario",
    promitente_vendedor: "Promitente Vendedor",
    promitente_comprador: "Promitente Comprador",
    corredor: "Corredor",
    entregante: "Entregante",
    receptor: "Receptor",
    propietario: "Propietario",
    administrador: "Administrador"
  };
  return roleNames[roleId] || roleId?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || roleId;
};

const IMPORTANCE_CONFIG = {
  critica: { label: "Crítica", bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" },
  alta: { label: "Alta", bg: "bg-orange-500/20", text: "text-orange-400", border: "border-orange-500/30" },
  media: { label: "Media", bg: "bg-yellow-500/20", text: "text-yellow-400", border: "border-yellow-500/30" },
  baja: { label: "Baja", bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" }
};

const ClauseComparatorPage = () => {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  
  // State for contract selection
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for comparison
  const [loading, setLoading] = useState(false);
  const [yourClauses, setYourClauses] = useState([]);
  const [counterpartyClauses, setCounterpartyClauses] = useState([]);
  const [yourRole, setYourRole] = useState('');
  const [counterpartyRole, setCounterpartyRole] = useState('');
  const [expandedLeft, setExpandedLeft] = useState(null);
  const [expandedRight, setExpandedRight] = useState(null);
  const [highlightMatches, setHighlightMatches] = useState(true);

  // Get base contract type for API call
  const getBaseContractType = (contractId) => {
    if (!contractId) return null;
    // Map specific contracts to their base types for the clause API
    if (contractId.includes('arriendo')) return 'arriendo';
    if (contractId.includes('compraventa') || contractId.includes('cesion')) return 'compraventa';
    if (contractId.includes('trabajo') || contractId.includes('laboral') || contractId.includes('honorarios') || contractId.includes('finiquito') || contractId.includes('teletrabajo') || contractId.includes('aprendizaje')) return 'laboral';
    return contractId.split('_')[0];
  };

  const fetchClauses = async () => {
    if (!selectedContract) return;
    
    const roles = selectedContract.roles;
    if (!roles || roles.length < 2) {
      toast.info('Este contrato no tiene roles definidos para comparación');
      return;
    }
    
    setLoading(true);
    const role1 = roles[0];
    const role2 = roles[1];
    setYourRole(role1);
    setCounterpartyRole(role2);
    
    // Get base type for API
    const baseType = getBaseContractType(selectedContract.id);
    
    try {
      const [clauses1, clauses2] = await Promise.all([
        api.get(`/chilean-library/clauses/${baseType}?user_role=${role1}`).catch(() => ({ data: { protective_clauses: [] } })),
        api.get(`/chilean-library/clauses/${baseType}?user_role=${role2}`).catch(() => ({ data: { protective_clauses: [] } }))
      ]);
      
      setYourClauses(clauses1.data.protective_clauses || []);
      setCounterpartyClauses(clauses2.data.protective_clauses || []);
      
      if ((clauses1.data.protective_clauses?.length || 0) === 0 && (clauses2.data.protective_clauses?.length || 0) === 0) {
        toast.info('No hay cláusulas específicas para este tipo de contrato. Intente con Arriendo, Compraventa o Laboral.');
      }
    } catch (error) {
      console.error('Error fetching clauses:', error);
      toast.error('Error al cargar cláusulas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedContract) {
      fetchClauses();
    }
  }, [selectedContract]);

  const copyClause = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Cláusula copiada');
  };

  // Find common themes between clauses
  const findCommonThemes = () => {
    const yourTypes = new Set(yourClauses.map(c => c.type));
    const theirTypes = new Set(counterpartyClauses.map(c => c.type));
    const common = [...yourTypes].filter(t => theirTypes.has(t));
    return common;
  };

  const commonThemes = findCommonThemes();

  // Filter contracts by search
  const filteredCategories = Object.entries(CONTRACT_CATEGORIES).map(([catId, cat]) => ({
    ...cat,
    id: catId,
    contracts: cat.contracts.filter(c => 
      searchQuery === '' || 
      c.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.contracts.length > 0);

  // Reset to selection view
  const handleReset = () => {
    setSelectedContract(null);
    setSelectedCategory(null);
    setYourClauses([]);
    setCounterpartyClauses([]);
    setYourRole('');
    setCounterpartyRole('');
  };

  // Export comparison to PDF
  const [exporting, setExporting] = useState(false);
  
  const handleExportPDF = async () => {
    if (!selectedContract || yourClauses.length === 0) {
      toast.error('Seleccione un contrato para exportar');
      return;
    }
    
    setExporting(true);
    try {
      const response = await api.post('/export/clause-comparison/pdf', {
        contract_name: selectedContract.name,
        contract_type: selectedContract.id,
        your_role: yourRole,
        counterparty_role: counterpartyRole,
        your_clauses: yourClauses,
        counterparty_clauses: counterpartyClauses
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comparacion_${selectedContract.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/tools" className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Herramientas</span>
            </Link>
            <div className="flex items-center space-x-3">
              <ArrowLeftRight className="w-6 h-6 text-purple-500" />
              <span className="font-semibold font-['Outfit']">Comparador de Cláusulas Universal</span>
            </div>
            <div />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Contract Selection View */}
        {!selectedContract && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold font-['Outfit'] mb-3">
                Comparador de Cláusulas Universal
              </h1>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Compara las cláusulas que te protegen versus las que protegen a la contraparte en cualquier tipo de contrato chileno.
                Identifica puntos de negociación y prepárate mejor.
              </p>
            </div>

            {/* Search */}
            <div className="max-w-xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  placeholder="Buscar tipo de contrato..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="search-contract-type"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Categories Grid */}
            <div className="space-y-6">
              {filteredCategories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <div key={category.id} className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
                    <div className={`flex items-center gap-3 px-5 py-4 ${category.bgColor}`}>
                      <IconComponent className={`w-5 h-5 ${category.color}`} />
                      <span className={`font-semibold ${category.color}`}>{category.name}</span>
                      <span className="ml-auto text-xs text-slate-500">{category.contracts.length} contratos</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                      {category.contracts.map((contract) => (
                        <button
                          key={contract.id}
                          onClick={() => setSelectedContract(contract)}
                          className="flex items-center justify-between p-4 rounded-lg border border-slate-700 hover:border-purple-500/50 bg-slate-900/50 transition-all text-left group"
                          data-testid={`select-${contract.id}`}
                        >
                          <div>
                            <span className="font-medium text-sm group-hover:text-purple-300 transition-colors">{contract.name}</span>
                            <p className="text-xs text-slate-500 mt-1">
                              {contract.roles.map(r => getRoleDisplayName(r)).join(' vs ')}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-purple-400 transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredCategories.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No se encontraron contratos que coincidan con "{searchQuery}"</p>
              </div>
            )}

            {/* Quick Stats */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {Object.values(CONTRACT_CATEGORIES).reduce((acc, cat) => acc + cat.contracts.length, 0)}
                </div>
                <div className="text-xs text-slate-500">Tipos de Contrato</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-400">{Object.keys(CONTRACT_CATEGORIES).length}</div>
                <div className="text-xs text-slate-500">Categorías</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-400">60+</div>
                <div className="text-xs text-slate-500">Cláusulas Analizables</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-400">100%</div>
                <div className="text-xs text-slate-500">Ley Chilena</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Comparison View */}
        {selectedContract && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <button
                  onClick={handleReset}
                  className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Cambiar contrato
                </button>
                <h2 className="text-xl font-bold">
                  {selectedContract.name}
                </h2>
                <p className="text-sm text-slate-500">
                  {getRoleDisplayName(yourRole)} vs {getRoleDisplayName(counterpartyRole)}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleExportPDF}
                  disabled={exporting || loading || yourClauses.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                  data-testid="export-pdf-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Exportar PDF
                </button>
                <label className="flex items-center gap-2 text-sm text-slate-400">
                  <input
                    type="checkbox"
                    checked={highlightMatches}
                    onChange={(e) => setHighlightMatches(e.target.checked)}
                    className="rounded border-slate-600"
                  />
                  Resaltar temas
                </label>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <Loader2 className="w-10 h-10 animate-spin mx-auto text-purple-500 mb-4" />
                <p className="text-slate-400">Cargando cláusulas para comparación...</p>
              </div>
            ) : (
              <>
                {/* Common Themes Banner */}
                {commonThemes.length > 0 && highlightMatches && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                      <span className="font-medium text-purple-300">Temas en Común</span>
                    </div>
                    <p className="text-sm text-purple-200/70">
                      Ambas partes tienen interés en protegerse en estos temas: 
                      <span className="font-medium"> {commonThemes.join(', ')}</span>.
                      Estos serán puntos de negociación importantes.
                    </p>
                  </div>
                )}

                {/* Side by Side Comparison */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Your Clauses (Left) */}
                  <div>
                    <div className="flex items-center gap-3 mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <ThumbsUp className="w-5 h-5 text-emerald-400" />
                      <div>
                        <h3 className="font-semibold text-emerald-300">Cláusulas que te Protegen</h3>
                        <p className="text-xs text-emerald-200/70">Como {getRoleDisplayName(yourRole)}</p>
                      </div>
                      <span className="ml-auto bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded text-sm">
                        {yourClauses.length}
                      </span>
                    </div>

                    {yourClauses.length === 0 ? (
                      <div className="text-center py-12 text-slate-500 bg-slate-800/30 rounded-xl border border-slate-700">
                        <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p>No hay cláusulas específicas para este rol.</p>
                        <p className="text-xs mt-2">Pruebe con Arriendo, Compraventa o Laboral.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {yourClauses.map((clause, index) => {
                          const isExpanded = expandedLeft === index;
                          const isCommon = highlightMatches && commonThemes.includes(clause.type);
                          const importance = IMPORTANCE_CONFIG[clause.importance] || IMPORTANCE_CONFIG.media;

                          return (
                            <div
                              key={index}
                              className={`bg-slate-800/50 rounded-xl border transition-all ${
                                isCommon ? 'border-purple-500/50 bg-purple-500/5' : 'border-slate-700'
                              }`}
                            >
                              <div
                                className="p-4 cursor-pointer"
                                onClick={() => setExpandedLeft(isExpanded ? null : index)}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                      <span className={`text-xs px-2 py-0.5 rounded ${importance.bg} ${importance.text}`}>
                                        {importance.label}
                                      </span>
                                      {isCommon && (
                                        <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                                          Tema común
                                        </span>
                                      )}
                                    </div>
                                    <h4 className="font-medium text-emerald-300">{clause.name || clause.title}</h4>
                                    <p className="text-sm text-slate-400 mt-1">{clause.reason || ''}</p>
                                  </div>
                                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </div>

                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="border-t border-slate-700"
                                  >
                                    <div className="p-4 bg-slate-900/50 space-y-3">
                                      <div>
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs text-slate-500">Texto de la cláusula:</span>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); copyClause(clause.content); }}
                                            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                          >
                                            <Copy className="w-3 h-3" /> Copiar
                                          </button>
                                        </div>
                                        <div className="bg-slate-800 rounded p-2 text-sm text-slate-300 font-mono max-h-32 overflow-y-auto">
                                          {clause.content}
                                        </div>
                                      </div>
                                      {clause.legal_basis && (
                                        <div className="flex items-center gap-2 text-xs text-slate-500">
                                          <Scale className="w-3 h-3" />
                                          {clause.legal_basis}
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Counterparty Clauses (Right) */}
                  <div>
                    <div className="flex items-center gap-3 mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <ThumbsDown className="w-5 h-5 text-amber-400" />
                      <div>
                        <h3 className="font-semibold text-amber-300">Cláusulas que Protegen a la Contraparte</h3>
                        <p className="text-xs text-amber-200/70">Como {getRoleDisplayName(counterpartyRole)}</p>
                      </div>
                      <span className="ml-auto bg-amber-500/30 text-amber-300 px-2 py-0.5 rounded text-sm">
                        {counterpartyClauses.length}
                      </span>
                    </div>

                    {counterpartyClauses.length === 0 ? (
                      <div className="text-center py-12 text-slate-500 bg-slate-800/30 rounded-xl border border-slate-700">
                        <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p>No hay cláusulas específicas para la contraparte.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {counterpartyClauses.map((clause, index) => {
                          const isExpanded = expandedRight === index;
                          const isCommon = highlightMatches && commonThemes.includes(clause.type);
                          const importance = IMPORTANCE_CONFIG[clause.importance] || IMPORTANCE_CONFIG.media;

                          return (
                            <div
                              key={index}
                              className={`bg-slate-800/50 rounded-xl border transition-all ${
                                isCommon ? 'border-purple-500/50 bg-purple-500/5' : 'border-slate-700'
                              }`}
                            >
                              <div
                                className="p-4 cursor-pointer"
                                onClick={() => setExpandedRight(isExpanded ? null : index)}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                      <span className={`text-xs px-2 py-0.5 rounded ${importance.bg} ${importance.text}`}>
                                        {importance.label}
                                      </span>
                                      {isCommon && (
                                        <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                                          Tema común
                                        </span>
                                      )}
                                    </div>
                                    <h4 className="font-medium text-amber-300">{clause.name || clause.title}</h4>
                                    <p className="text-sm text-slate-400 mt-1">{clause.reason || ''}</p>
                                  </div>
                                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </div>

                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="border-t border-slate-700"
                                  >
                                    <div className="p-4 bg-slate-900/50 space-y-3">
                                      <div>
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs text-slate-500">Texto de la cláusula:</span>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); copyClause(clause.content); }}
                                            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                          >
                                            <Copy className="w-3 h-3" /> Copiar
                                          </button>
                                        </div>
                                        <div className="bg-slate-800 rounded p-2 text-sm text-slate-300 font-mono max-h-32 overflow-y-auto">
                                          {clause.content}
                                        </div>
                                      </div>
                                      {clause.legal_basis && (
                                        <div className="flex items-center gap-2 text-xs text-slate-500">
                                          <Scale className="w-3 h-3" />
                                          {clause.legal_basis}
                                        </div>
                                      )}
                                      <div className="pt-2 border-t border-slate-700">
                                        <span className="text-xs text-amber-400 flex items-center gap-1">
                                          <AlertTriangle className="w-3 h-3" />
                                          Esta cláusula protege a la contraparte. Prepara tu respuesta.
                                        </span>
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-emerald-400">{yourClauses.length}</div>
                    <div className="text-sm text-slate-400">Cláusulas para ti</div>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-amber-400">{counterpartyClauses.length}</div>
                    <div className="text-sm text-slate-400">Cláusulas contraparte</div>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-purple-400">{commonThemes.length}</div>
                    <div className="text-sm text-slate-400">Temas en común</div>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-blue-400">
                      {yourClauses.filter(c => c.importance === 'critica').length + 
                       counterpartyClauses.filter(c => c.importance === 'critica').length}
                    </div>
                    <div className="text-sm text-slate-400">Cláusulas críticas</div>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-8 text-center">
                  <Link
                    to={`/negociacion?type=${getBaseContractType(selectedContract?.id)}&role=${yourRole}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors"
                  >
                    <Sparkles className="w-5 h-5" />
                    Continuar al Asistente de Negociación
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ClauseComparatorPage;
